import { motion } from 'framer-motion';
import { ArrowRight, Boxes, Gauge, GripVertical } from 'lucide-react';
import { useCallback, useEffect } from 'react';
import Button from '@components/ui/Button';
import Icon from '@components/ui/Icon';
import { useUIStore } from '@stores/useUIStore';
import { track } from '@utils/analytics';

/**
 * SplashScreen — the first-load hero overlay. Dismissed by clicking
 * "Get Started", by pressing Enter, or by pressing Escape. "Learn more"
 * opens the About modal without dismissing the splash.
 *
 * Visual: a centered card above a blurred, tech-grid backdrop with a
 * soft green radial wash, the pulsing "S" logo, product wordmark,
 * tagline, CTAs, feature chips, and a keyboard hint.
 */

const CHIPS: readonly { icon: typeof Gauge; label: string }[] = [
  { icon: Gauge, label: 'RT60 engine · 6 bands' },
  { icon: Boxes, label: '17 treatment objects' },
  { icon: GripVertical, label: 'Drag-to-place' },
];

export default function SplashScreen() {
  const dismissStore = useUIStore((s) => s.dismissSplash);
  const showAbout = useUIStore((s) => s.showAbout);

  const dismissVia = useCallback(
    (via: 'cta' | 'enter' | 'esc') => {
      track('splash_dismissed', { via });
      dismissStore();
    },
    [dismissStore],
  );

  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Enter') {
        e.preventDefault();
        dismissVia('enter');
      } else if (e.key === 'Escape') {
        e.preventDefault();
        dismissVia('esc');
      }
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [dismissVia]);

  return (
    <motion.div
      role="dialog"
      aria-modal="true"
      aria-label="Welcome to StudioShell"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.25, ease: 'easeOut' }}
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 500,
        display: 'grid',
        placeItems: 'center',
        padding: 'var(--space-2xl)',
        background: 'rgba(6, 26, 34, 0.85)',
        backdropFilter: 'blur(10px)',
        WebkitBackdropFilter: 'blur(10px)',
      }}
    >
      {/* Subtle technical grid */}
      <div
        aria-hidden
        style={{
          position: 'absolute',
          inset: 0,
          pointerEvents: 'none',
          opacity: 0.4,
          backgroundImage:
            'linear-gradient(rgba(255,255,255,0.025) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.025) 1px, transparent 1px)',
          backgroundSize: '48px 48px',
        }}
      />
      <div
        aria-hidden
        style={{
          position: 'absolute',
          inset: 0,
          pointerEvents: 'none',
          background:
            'radial-gradient(ellipse at center, rgba(112,148,36,0.08) 0%, transparent 55%)',
        }}
      />

      <motion.div
        initial={{ opacity: 0, y: 12, scale: 0.98 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        exit={{ opacity: 0, y: 6, scale: 0.98 }}
        transition={{ duration: 0.35, ease: 'easeOut' }}
        style={{
          position: 'relative',
          width: '100%',
          maxWidth: 520,
          padding: '40px 36px 32px',
          background: 'var(--bg-panel)',
          border: '1px solid var(--border-mid)',
          borderRadius: 'var(--radius-lg)',
          boxShadow:
            '0 24px 60px rgba(0, 0, 0, 0.5), 0 0 40px rgba(112, 148, 36, 0.08)',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: 'var(--space-2xl)',
          textAlign: 'center',
        }}
      >
        {/* Pulsing logo mark with pulse dot */}
        <motion.div
          aria-hidden
          animate={{ scale: [1, 1.04, 1], opacity: [1, 0.92, 1] }}
          transition={{ duration: 2.2, repeat: Infinity, ease: 'easeInOut' }}
          style={{
            position: 'relative',
            width: 72,
            height: 72,
            borderRadius: 'var(--radius-xl)',
            background: 'var(--accent-green)',
            display: 'grid',
            placeItems: 'center',
            fontFamily: 'var(--font-mono)',
            fontWeight: 700,
            fontSize: 40,
            color: '#fff',
            boxShadow: '0 0 40px rgba(112, 148, 36, 0.25)',
          }}
        >
          S
          <span
            aria-hidden
            style={{
              position: 'absolute',
              top: 6,
              right: 6,
              width: 6,
              height: 6,
              borderRadius: '50%',
              background: '#fff',
              opacity: 0.9,
            }}
          />
        </motion.div>

        {/* Wordmark + tagline */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-sm)', alignItems: 'center' }}>
          <h1
            style={{
              fontFamily: 'var(--font-sans)',
              fontWeight: 700,
              fontSize: 32,
              letterSpacing: '-0.5px',
              margin: 0,
              lineHeight: 1,
              color: 'var(--accent-green)',
            }}
          >
            StudioShell
          </h1>
          <div
            style={{
              fontFamily: 'var(--font-sans)',
              fontSize: 11,
              fontWeight: 500,
              letterSpacing: '1.8px',
              textTransform: 'uppercase',
              color: 'var(--text-muted)',
              lineHeight: 1,
            }}
          >
            Acoustic Room Simulator
          </div>
        </div>

        <p
          style={{
            margin: 0,
            fontSize: 14,
            lineHeight: 1.55,
            color: 'var(--text-primary)',
            maxWidth: 380,
          }}
        >
          Design your studio in 3D. See your RT60 in real time.
        </p>

        {/* CTAs */}
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 'var(--space-md)', width: '100%' }}>
          <Button
            variant="primary"
            size="lg"
            icon={<Icon icon={ArrowRight} size={16} />}
            iconPosition="right"
            onClick={() => dismissVia('cta')}
          >
            Get Started
          </Button>
          <button
            type="button"
            onClick={showAbout}
            style={{
              background: 'transparent',
              border: 'none',
              cursor: 'pointer',
              color: 'var(--accent-cyan)',
              fontFamily: 'var(--font-sans)',
              fontSize: 12,
              fontWeight: 500,
              letterSpacing: 0,
              padding: '4px 8px',
              lineHeight: 1,
            }}
          >
            Learn more <span style={{ opacity: 0.7 }}>↗</span>
          </button>
        </div>

        {/* Feature chips */}
        <div
          style={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: 'var(--space-xs)',
            justifyContent: 'center',
            paddingTop: 'var(--space-xl)',
            borderTop: '1px solid var(--border-dim)',
            width: '100%',
          }}
        >
          {CHIPS.map((chip) => (
            <span
              key={chip.label}
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 6,
                padding: '5px 10px',
                borderRadius: 999,
                background: 'var(--bg-subtle)',
                border: '1px solid var(--border-mid)',
                fontSize: 10,
                fontWeight: 500,
                letterSpacing: '1.2px',
                textTransform: 'uppercase',
                color: 'var(--text-muted)',
                lineHeight: 1,
                whiteSpace: 'nowrap',
              }}
            >
              <Icon icon={chip.icon} size={11} color="var(--text-hint)" />
              {chip.label}
            </span>
          ))}
        </div>

        {/* Keyboard hint */}
        <div
          style={{
            fontFamily: 'var(--font-mono)',
            fontSize: 10,
            color: 'var(--text-hint)',
            letterSpacing: 0,
            display: 'flex',
            alignItems: 'center',
            gap: 8,
          }}
        >
          <Kbd>Enter</Kbd>
          <span style={{ opacity: 0.6 }}>or</span>
          <Kbd>Esc</Kbd>
          <span>to continue</span>
        </div>

        <div
          style={{
            fontFamily: 'var(--font-mono)',
            fontSize: 10,
            color: 'var(--text-hint)',
            letterSpacing: 0,
          }}
        >
          free · donation-supported · no signup
        </div>
      </motion.div>
    </motion.div>
  );
}

/* ─── Kbd key pill ───────────────────────────────────────────── */

function Kbd({ children }: { children: React.ReactNode }) {
  return (
    <span
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        padding: '2px 6px',
        borderRadius: 4,
        background: 'var(--bg-subtle)',
        border: '1px solid var(--border-mid)',
        fontFamily: 'var(--font-mono)',
        fontSize: 10,
        color: 'var(--text-muted)',
        lineHeight: 1,
      }}
    >
      {children}
    </span>
  );
}
